import { useState } from "react";
import { ChevronDown, MoveDown, Pencil } from "lucide-react";
import LiveRouteMap from "@/components/LiveRouteMap";
import { formatChf } from "@/lib/data";
import { useLang } from "@/lib/i18n";

const WAIT_LABEL = { fr: "Attente", en: "Wait", de: "Wartezeit", ru: "Ожидание", zh: "等候" };
const MIN_LABEL = { fr: "min", en: "min", de: "Min.", ru: "мин", zh: "分钟" };
const VIA_LABEL = { fr: "Via", en: "Via", de: "Über", ru: "Через", zh: "途经" };

const activeStopsOf = (form) => (form.trip_type !== "hourly" ? (form.stops || []) : [])
  .filter((s) => (s.address || "").trim().length > 2);

const EmptyRoute = () => (
  <div className="flex h-40 items-center justify-center rounded-2xl border border-ink/10 bg-cloud" data-testid="trip-empty-route">
    <div className="flex items-center gap-3">
      <span className="h-3 w-3 rounded-full bg-miles shadow-[0_0_0_8px_rgba(36,87,255,0.08)]" />
      <span className="h-px w-24 border-t border-dashed border-miles/35" />
      <span className="h-3 w-3 rounded-full border-2 border-ink/20 bg-white" />
    </div>
  </div>
);

const SummaryRows = ({ form, estimate, t, lang = "fr", hidePrice = false, promo = null, showStopsRow = false }) => {

const partyLine = (form, t) => `${form.passengers} ${form.passengers > 1 ? t("bk.pax_many") : t("bk.pax_one")} · ${form.luggage} ${form.luggage > 1 ? t("bk.bag_many") : t("bk.bag_one")}`;

  const stops = activeStopsOf(form);
  const viaText = stops.map((s) => {
    const w = Number(s.wait_minutes) || 0;
    return w > 0 ? `${s.address} (${w} ${MIN_LABEL[lang] || "min"})` : s.address;
  }).join(" · ");
  const rows = [
    form.date && form.time ? `${form.date} · ${form.time}` : null,
    showStopsRow && viaText ? `${VIA_LABEL[lang] || "Via"} : ${viaText}` : null,
    partyLine(form, t),
    // multi-véhicules : une ligne par catégorie avec sa quantité
    ...((form.vehicles || []).length
      ? form.vehicles.map((v) => `${v.count}× ${t(`veh.${v.category}.name`)}`)
      : form.vehicle_category
        ? [t(`veh.${form.vehicle_category}.name`), t(`veh.${form.vehicle_category}.model`)]
        : []),
  ].filter(Boolean);
  const waitFee = estimate?.waiting_fee_chf || 0;

  return (
    <div className="space-y-2.5">
      {rows.map((row) => <p key={row} className="text-sm text-slate">{row}</p>)}
      {!hidePrice && form.price_chf ? (
        <div className="mt-4 border-t border-ink/10 pt-4">
          {waitFee > 0 ? (
            <div className="mb-3 flex items-center justify-between text-sm" data-testid="summary-waiting">
              <span className="text-slate">{WAIT_LABEL[lang] || "Attente"} ({estimate.waiting_minutes} {MIN_LABEL[lang] || "min"})</span>
              <span className="font-mono text-slate">+{formatChf(waitFee)}</span>
            </div>
          ) : null}
          {promo ? (
            <div className="mb-4 space-y-1.5" data-testid="summary-promo">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate">{t("bk.fixed_price")}</span>
                <span className="font-mono text-slate line-through">{formatChf(promo.base_chf)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-emerald-600">{promo.code} · −{promo.percent_off} %</span>
                <span className="font-mono text-emerald-600">−{formatChf(promo.discount_chf)}</span>
              </div>
            </div>
          ) : null}
          <p className="text-sm text-slate">{promo ? t("bk.promo_total") : t("bk.fixed_price")}</p>
          <p className="mt-1 text-3xl font-semibold tracking-[-0.04em] text-ink" data-testid="summary-price">{formatChf(form.price_chf)}</p>
        </div>
      ) : null}
    </div>
  );
};

export const MobileTripSummary = ({ form, estimate }) => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div className="mx-5 mt-4 rounded-2xl border border-ink/10 bg-white p-4 shadow-sm lg:hidden" data-testid="trip-summary-mobile">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="trip-summary-toggle"
        className="flex w-full items-center justify-between gap-3 text-left">
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-ink">
            {form.pickup || t("bk.rail_from")} → {form.dropoff || t("bk.rail_to")}
          </span>
          <span className="mt-1 block text-xs text-slate">
            {form.price_chf ? formatChf(form.price_chf) : t("bk.trip_summary_pending")}
          </span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      {open && (
        <div className="mt-4 border-t border-ink/10 pt-4">
          {estimate?.route && form.trip_type !== "hourly" ? <LiveRouteMap geometry={estimate.route} height="h-56" testId="booking-route-map-mobile" /> : <EmptyRoute />}
          {estimate && form.trip_type !== "hourly" && (
            <p className="mt-3 font-mono text-xs text-slate" data-testid="booking-route-map-meta-mobile">{estimate.km} km · {estimate.minutes} min</p>
          )}
          <div className="mt-4"><SummaryRows form={form} estimate={estimate} t={t} lang={lang} showStopsRow /></div>
        </div>
      )}
    </div>
  );
};

export default function TripSummary({ form, estimate, onEdit, hidePrice = false, promo = null, sticky = true }) {
  const { t, lang } = useLang();
  const stops = activeStopsOf(form);
  return (
    <aside className={`${sticky ? "sticky top-28 " : ""}hidden rounded-[1.5rem] border border-ink/10 bg-white p-6 shadow-[0_24px_70px_-42px_rgba(16,24,40,0.25)] lg:block`} data-testid="trip-summary">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-ink">{t("bk.trip_card_title")}</h2>
          <p className="mt-1 text-sm text-slate">{t("bk.trip_card_sub")}</p>
        </div>
        {form.pickup && (
          <button type="button" onClick={() => onEdit(0)} className="flex items-center gap-1 text-xs font-medium text-miles" data-testid="trip-edit-route">
            <Pencil className="h-3 w-3" /> {t("bk.edit")}
          </button>
        )}
      </div>
      <div className="mt-5">
        {estimate?.route && form.trip_type !== "hourly" ? <LiveRouteMap geometry={estimate.route} height="h-64" testId="booking-route-map" /> : <EmptyRoute />}
        {estimate && form.trip_type !== "hourly" && (
          <p className="mt-3 text-center font-mono text-xs text-slate" data-testid="booking-route-map-meta">{estimate.km} km · {estimate.minutes} min</p>
        )}
      </div>
      <div className="mt-5 flex items-start gap-3" data-testid="trip-route-rail">
        <div className="mt-1 flex flex-col items-center">
          <span className="h-2.5 w-2.5 rounded-full bg-miles" />
          <span className="my-1 h-8 w-px border-l border-dashed border-miles/30" />
          {stops.map((s, i) => (
            <span key={`d${i}`} className="flex flex-col items-center">
              <span className="h-2 w-2 rounded-full border-2 border-miles bg-white" />
              <span className="my-1 h-8 w-px border-l border-dashed border-miles/30" />
            </span>
          ))}
          <MoveDown className="h-3.5 w-3.5 text-miles" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`truncate text-sm font-semibold ${form.pickup ? "text-ink" : "text-slate/45"}`}>{form.pickup || t("bk.rail_from")}</p>
          {stops.map((s, i) => {
            const w = Number(s.wait_minutes) || 0;
            return (
              <p key={`s${i}`} className="mt-5 truncate text-sm font-medium text-ink" data-testid={`trip-summary-stop-${i}`}>
                {s.address}
                {w > 0 && <span className="ml-1 font-normal text-slate">· {w} {MIN_LABEL[lang] || "min"} {WAIT_LABEL[lang]?.toLowerCase() || ""}</span>}
              </p>
            );
          })}
          <p className={`mt-5 truncate text-sm font-semibold ${form.dropoff || form.trip_type === "hourly" ? "text-ink" : "text-slate/45"}`}>
            {form.trip_type === "hourly" ? t("bk.hourly_word") : form.dropoff || t("bk.rail_to")}
          </p>
          {form.trip_type === "round_trip" && form.stops_on_return && stops.length > 0 && (
            <p className="mt-2 text-xs text-slate">↩ {lang === "de" ? "Stopps auch auf der Rückfahrt" : lang === "en" ? "Stops also on return" : "Arrêts aussi au retour"}</p>
          )}
        </div>
      </div>
      <div className="mt-5 border-t border-ink/10 pt-5">
        <SummaryRows form={form} estimate={estimate} t={t} lang={lang} hidePrice={hidePrice} promo={promo} />
      </div>
    </aside>
  );
}
